import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getProfile, getDashboard, updateEmployment, downloadContract } from "@/api";
import BottomNav from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft, User, Shield, Phone, Mail, FileText, Briefcase,
  CheckCircle, Clock, XCircle, Edit2, Save, Loader2, Download,
  CreditCard, IndianRupee, Building, MapPin, Calendar, ChevronRight
} from "lucide-react";
import { LOGO_ICON_BLUE } from '@/components/SplashScreen';
import NotificationPreferencesCard from '@/components/NotificationPreferencesCard';

export default function CustomerProfile() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingEmployment, setEditingEmployment] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [empForm, setEmpForm] = useState({ employment_type: '', employer_name: '', monthly_income: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await getDashboard();
      setData(res.data);
      const p = res.data.profile || {};
      setEmpForm({
        employment_type: p.employment_type || '',
        employer_name: p.employer_name || '',
        monthly_income: p.monthly_income || ''
      });
    } catch {} finally { setLoading(false); }
  };

  const handleSaveEmployment = async () => {
    setSaving(true);
    try {
      await updateEmployment(empForm);
      toast.success("Employment details updated");
      setEditingEmployment(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };

  const handleDownloadContract = async (appId) => {
    setDownloading(appId);
    try {
      const res = await downloadContract(appId);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contract_${appId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Contract downloaded");
    } catch (err) {
      toast.error("Failed to download contract");
    } finally { setDownloading(null); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return dateStr.substring(0, 10); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1565C0 100%)' }}>
      <div className="flex flex-col items-center gap-4">
        <div className="spinner" style={{ borderTopColor: '#00BCD4' }} />
        <p className="text-white/70 text-sm">Loading profile...</p>
      </div>
    </div>
  );

  const profile = data?.profile || {};
  const subscription = data?.subscription;
  const applications = data?.applications || [];
  const documents = data?.documents || [];

  const StatusBadge = ({ verified, label }) => (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
      verified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
    }`}>
      {verified ? <CheckCircle className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
      {label}
    </span>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-24" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1565C0 100%)' }}>
        <div className="max-w-lg mx-auto px-5 py-4 flex items-center gap-3">
          <button onClick={() => navigate("/customer/dashboard")} className="text-white/70 hover:text-white transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white tracking-tight" style={{fontFamily:'Poppins, sans-serif'}}>My Profile</h1>
            <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-medium">Account Details</p>
          </div>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-5 py-6 space-y-5">
        {/* Profile Card */}
        <div className="rounded-2xl p-6 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #00BCD4 100%)' }}>
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <User className="h-8 w-8 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight">{profile.name || 'Customer'}</h2>
              <div className="flex items-center gap-2 text-white/80 text-sm mt-1">
                <Phone className="h-3.5 w-3.5" />
                <span>+91 {profile.mobile}</span>
              </div>
              {profile.email && (
                <div className="flex items-center gap-2 text-white/70 text-xs mt-0.5">
                  <Mail className="h-3 w-3" />
                  <span>{profile.email}</span>
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 text-xs text-white/60">
            <Calendar className="h-3 w-3" />
            <span>Member since {formatDate(profile.created_at)}</span>
          </div>
        </div>

        {/* KYC Verification Status */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="h-4 w-4 text-blue-600" />
            <h3 className="font-semibold text-gray-900 text-sm">KYC Verification</h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <CreditCard className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">PAN Verification</p>
                  {profile.pan_number && <p className="text-xs text-gray-500">{profile.pan_number}</p>}
                </div>
              </div>
              <StatusBadge verified={profile.pan_verified} label={profile.pan_verified ? 'Verified' : 'Pending'} />
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Aadhaar Verification</p>
                  <p className="text-xs text-gray-500">{profile.aadhaar_verified ? 'Verified by our team' : (profile.aadhaar_uploaded ? 'Pending review' : 'Not yet verified')}</p>
                </div>
              </div>
              <StatusBadge verified={profile.aadhaar_verified} label={profile.aadhaar_verified ? 'Verified' : 'Pending'} />
            </div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
                <p className="text-sm font-medium text-gray-900">Overall KYC Status</p>
              </div>
              <StatusBadge 
                verified={profile.kyc_status === 'verified'} 
                label={profile.kyc_status === 'verified' ? 'Verified' : profile.kyc_status || 'Pending'} 
              />
            </div>
          </div>
          {profile.kyc_status !== 'verified' && (
            <button onClick={() => navigate("/customer/onboarding")} className="mt-4 w-full py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, #1565C0, #00BCD4)' }}>
              Complete KYC <ChevronRight className="inline h-4 w-4 ml-1" />
            </button>
          )}
        </div>

        {/* Subscription Status */}
        {subscription && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="h-4 w-4 text-green-600" />
              <h3 className="font-semibold text-gray-900 text-sm">Subscription</h3>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900">{subscription.plan_name || 'EMI Support Plan'}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  ₹{Number(subscription.amount || 899).toLocaleString('en-IN')} <span className="text-gray-400">/ year</span>
                </p>
              </div>
              <StatusBadge verified={subscription.is_active} label={subscription.is_active ? 'Active' : 'Inactive'} />
            </div>
            {subscription.expires_at && (
              <div className="mt-3 bg-gray-50 rounded-xl p-3 flex items-center justify-between text-xs text-gray-600">
                <span>Expires: {formatDate(subscription.expires_at)}</span>
                {subscription.days_remaining !== undefined && (
                  <span className="font-semibold text-blue-600">{subscription.days_remaining} days left</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Employment Details */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4 text-amber-600" />
              <h3 className="font-semibold text-gray-900 text-sm">Employment Details</h3>
            </div>
            {!editingEmployment ? (
              <button onClick={() => setEditingEmployment(true)} className="flex items-center gap-1 text-xs text-blue-600 font-semibold hover:text-blue-700">
                <Edit2 className="h-3.5 w-3.5" /> Edit
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button onClick={() => { setEditingEmployment(false); const p2 = data?.profile || {}; setEmpForm({ employment_type: p2.employment_type || '', employer_name: p2.employer_name || '', monthly_income: p2.monthly_income || '' }); }} className="text-xs text-gray-500 font-semibold hover:text-gray-700">Cancel</button>
                <button onClick={handleSaveEmployment} disabled={saving} className="flex items-center gap-1 text-xs text-green-600 font-semibold hover:text-green-700 disabled:opacity-50">
                  {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                </button>
              </div>
            )}
          </div>
          
          {editingEmployment ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-gray-500 font-semibold">Employment Type</Label>
                <select value={empForm.employment_type} onChange={e => setEmpForm({...empForm, employment_type: e.target.value})} className="w-full mt-1 rounded-xl border border-gray-200 px-3 py-2.5 text-sm">
                  <option value="">Select</option>
                  <option value="salaried">Salaried</option>
                  <option value="self_employed">Self Employed</option>
                  <option value="business">Business Owner</option>
                  <option value="freelancer">Freelancer</option>
                  <option value="student">Student</option>
                  <option value="retired">Retired</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-gray-500 font-semibold">Employer Name</Label>
                <Input value={empForm.employer_name} onChange={e => setEmpForm({...empForm, employer_name: e.target.value})} placeholder="Company name" className="rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-xs text-gray-500 font-semibold">Monthly Income (₹)</Label>
                <Input type="number" value={empForm.monthly_income} onChange={e => setEmpForm({...empForm, monthly_income: e.target.value})} placeholder="e.g., 50000" className="rounded-xl mt-1" />
              </div>
            </div>
          ) : (
            <div className="space-y-2.5">
              <div className="flex justify-between py-1.5">
                <span className="text-xs text-gray-500">Type</span>
                <span className="text-sm font-medium text-gray-900 capitalize">{(profile.employment_type || 'Not set').replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between py-1.5 border-t border-gray-50">
                <span className="text-xs text-gray-500">Employer</span>
                <span className="text-sm font-medium text-gray-900">{profile.employer_name || 'Not set'}</span>
              </div>
              <div className="flex justify-between py-1.5 border-t border-gray-50">
                <span className="text-xs text-gray-500">Monthly Income</span>
                <span className="text-sm font-medium text-gray-900">
                  {profile.monthly_income ? `₹${Number(profile.monthly_income).toLocaleString('en-IN')}` : 'Not set'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Documents */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-indigo-600" />
              <h3 className="font-semibold text-gray-900 text-sm">My Documents</h3>
            </div>
            <span className="text-xs text-gray-400">{documents.length} uploaded</span>
          </div>
          
          {documents.length === 0 ? (
            <div className="text-center py-6">
              <FileText className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No documents uploaded yet</p>
              <button onClick={() => navigate("/customer/onboarding")} className="mt-2 text-xs text-blue-600 font-semibold hover:underline">
                Upload in Onboarding
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.map(doc => (
                <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <FileText className="h-4 w-4 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 capitalize">{(doc.doc_type || 'document').replace('_', ' ')}</p>
                      <p className="text-xs text-gray-400">{formatDate(doc.created_at)}</p>
                    </div>
                  </div>
                  {doc.file_url && (
                    <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 font-semibold hover:underline">View</a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contract Downloads */}
        {applications.filter(a => a.status === 'approved' || a.status === 'disbursed').length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Download className="h-4 w-4 text-teal-600" />
              <h3 className="font-semibold text-gray-900 text-sm">Contracts</h3>
            </div>
            <div className="space-y-2">
              {applications
                .filter(a => a.status === 'approved' || a.status === 'disbursed')
                .map(app => (
                  <div key={app.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{app.application_number || 'Application'}</p>
                      <p className="text-xs text-gray-500">
                        ₹{Number(app.amount_requested || app.loan_amount || 0).toLocaleString('en-IN')} • {app.tenure || 1}mo
                      </p>
                    </div>
                    <button
                      onClick={() => handleDownloadContract(app.id)}
                      disabled={downloading === app.id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white disabled:opacity-50"
                      style={{ background: 'linear-gradient(135deg, #1565C0, #00BCD4)' }}
                    >
                      {downloading === app.id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <><Download className="h-3 w-3" /> Download</>
                      )}
                    </button>
                  </div>
                ))
              }
            </div>
          </div>
        )}

        {/* Account Actions */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 space-y-3">
          <button onClick={() => navigate("/customer/onboarding")} className="w-full flex items-center justify-between py-2.5 text-sm text-gray-700 hover:text-blue-600 transition-colors">
            <span className="font-medium">Edit Onboarding Details</span>
            <ChevronRight className="h-4 w-4" />
          </button>
          <hr className="border-gray-100" />
          <button onClick={() => { logout(); navigate("/"); }} className="w-full flex items-center justify-between py-2.5 text-sm text-red-600 hover:text-red-700 transition-colors">
            <span className="font-medium">Logout</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Push notification preferences */}
        <NotificationPreferencesCard />
      </div>

      <BottomNav />
    </div>
  );
}
